import { useState, Suspense, useEffect, useCallback, useContext } from "react"
import { Container, Row, Col, Spinner, Modal, ProgressBar, Image as BootstrapImage } from "react-bootstrap"
import { Swiper, SwiperSlide } from "swiper/react"
import { toast } from "react-toastify"
import ReactPlayer from "react-player"
import styled from "styled-components"
import Image from "next/image"
import "swiper/css"
import "swiper/css/navigation"
import CreateComment from "../../Comment/components/CreateComment"
import ErrorFallback from "@modules/Common/components/ErrorFallback"
import Comments from "@modules/Comment/components/Comments"
import { CURRENT_USER } from "@modules/User/graphql"
import ErrorBoundary from "@modules/Common/components/ErrorBoundary"
import { CommentActivityType, StudentActivityWithQuestionsQuery, StudentNextRandomActivityQuery, useActivityQuestionSubmitMutation, useStudentActivityWithQuestionsQuery, useStudentNextRandomActivityLazyQuery, useValidateActivityQuestionAnswerMutation, ValidateActivityQuestionAnswerOutput, ValidateActivityQuestionSubmitInput } from "@generated/graphql"
import { DISPLAY_STATE } from "../constants"
import { isEmptyString } from "@utils/stringUtils"
import { useMediaQuery } from "react-responsive"
import { NEXT_PUBLIC_SERVER_FILE_ENDPOINT } from "@utils/constant"
import StudentSelfComments from "@modules/Comment/components/StudentSelfComments"
import { addEvent } from "@utils/gtm"
import { AppContext } from "AppContext"

const StyledSkillNameSpan = styled.div`
  text-align: center;
  display: inline-block;
  font-weight: 700;
  background:url(/assets/svg/ActivityDetailSkillName.svg);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  color: white;
  border-radius: 8px;
  margin: 5px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 1.3em;
`

const StyledAnswerCol = styled(Col)`
  padding: 32px 48px 32px 48px;
  @media (max-width: 601px) {
    padding: 16px;
  }
`

const StyledAnswerHeader = styled.h2`
  color: #6630da;
  font-weight: 700;
  font-size: 72px;
  text-transform: uppercase;
  line-height: 64px;
  @media (max-width: 601px) {
    font-size: 48px;
    line-height: 48px;
  }
`

const StyledHeader = styled.h1`
  color: #6530D9;
  font-weight: bold;
`

interface AnswerImageProps {
  src: string
  mobileSrc: string
}

const StyledAnswerImage = styled.div<AnswerImageProps>`
  background-image: url(${props => props.src});
  background-size: 100% 100%;
  background-repeat: no-repeat;
  min-height: 100%;
  @media (max-width: 601px) {
    min-height: 280px;
    background-image: url(${props => props.mobileSrc});
  }
`

const NavButton = styled.button`
  background: url(/assets/img/button.png);
  background-repeat: no-repeat;
  background-position: center;
  padding: 0.5em 2em;
  background-size: 100% 100%;
  border: none;
  font-weight: 700;
  font-size: 1.3em;
  margin: 0.75em 0.1em;
  @media (max-width: 601px) {
    font-size: 1em;
    padding: 0.5em 1.5em;
  }
`

const QuestionInputContainer = styled.div`
 position: relative;
 text-align: left;
`

const HiddenQuestionInput = styled.input`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  margin: 0;
  cursor: pointer;
`

const TextStyledInput = styled.input.attrs(
  { type: "text" }
)`
  cursor: pointer;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
`

const QuestionInputLabel = styled.span`
  display: flex;
  flex-direction: column;
  align-items: left;
  justify-content: center;
  font-weight: bolder;
  font-size: 20px;
  text-align: left;
  background-color: white;
  color: rgb(102, 48, 218);
  border: none;
  box-shadow: none;
  margin: 16px 16px 0px 0px;
  padding: 8px;
  word-break: break-word;
  border-radius: 8px;
  outline: none !important;

  ${HiddenQuestionInput}:hover + && {
    box-shadow: none;
    background-color: rgb(102, 48, 218);
    color: white;
    border: none;
    outline: none !important;
  }

  ${HiddenQuestionInput}:checked + && {
    background-color: rgb(102, 48, 218);
    color: white;
  }
`

const StyledBlankContainer = styled.div`
& > span {
  & > p {
    display: inline;
    font-size: 1.1rem;
    line-height: 2em;
  }
}
`

interface IActivityQuestionView {
  slug: string
  displayState: DISPLAY_STATE
  setShowConfirmModal: any
  setDisplayState: any
}

export enum FOOTER_BUTTON_STATE {
  HIDE,
  CONTINUE,
  CONTINUE_DISABLED,
  QA_ENABLED,
  QA_DISABLED,
  CHALLENGE_SUBMIT,
  QA_FAILED
}

export default function ActivityQuestionView({ slug, setDisplayState, displayState, setShowConfirmModal }: IActivityQuestionView) {
  const { state } = useContext(AppContext)
  const [swiper, setSwiper] = useState<any>()
  const [progress, setProgress] = useState<number>()
  const [showNextButton, setShowNextButton] = useState(false)
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [showUserSubmission, setShowUserSubmission] = useState(false)
  const [nextRandomActivity, setNextRandomActivity] = useState<StudentNextRandomActivityQuery["studentNextRandomActivity"]>()
  const [validateActivityQuestionAnswer] = useValidateActivityQuestionAnswerMutation()
  const [activityQuestionSubmit] = useActivityQuestionSubmitMutation()
  const [activity, setActivity] = useState<StudentActivityWithQuestionsQuery["studentActivity"] | null>(null)
  const [questionResponse, setQuestionResponse] = useState<ValidateActivityQuestionAnswerOutput | null>(null)
  const [footerButtonState, setFooterButtonState] = useState<FOOTER_BUTTON_STATE>(FOOTER_BUTTON_STATE.CONTINUE)
  const [answerSubmissions, setAnswerSubmissions] = useState<ValidateActivityQuestionSubmitInput[]>([])

  const [closeModalAudio, setCloseModalAudio] = useState<HTMLAudioElement | null>(null)
  const [individualCorrectAudio, setIndividualCorrectAudio] = useState<HTMLAudioElement | null>(null)
  const [incorrectAudio, setIncorrectAudio] = useState<HTMLAudioElement | null>(null)
  const [qaXpsAudio, setQaXpsAudio] = useState<HTMLAudioElement | null>(null)
  const [continueAudio, setContinueAudio] = useState<HTMLAudioElement | null>(null)
  const [commentSubmitAudio, setCommentSubmitAudio] = useState<HTMLAudioElement | null>(null)

  const [studentNextRandomActivityLazyQuery] = useStudentNextRandomActivityLazyQuery()

  const windowAlert = useCallback((event: any) => {
    const confirmationMessage = "If you leave before saving, your changes will be lost.";
    (event || window.event).returnValue = confirmationMessage
    return confirmationMessage
  }, [])

  useEffect(() => {
    setCloseModalAudio(new Audio("/assets/sound/qa-close-button.mp3"))
    setIndividualCorrectAudio(new Audio("/assets/sound/correct-answer.mp3"))
    setIncorrectAudio(new Audio("/assets/sound/incorrect-answer.mp3"))
    setQaXpsAudio(new Audio("/assets/sound/qa-xps.mp3"))
    setContinueAudio(new Audio("/assets/sound/continue.mp3"))
    setCommentSubmitAudio(new Audio("/assets/sound/successful-submission.mp3"))

    window.addEventListener("beforeunload", windowAlert)
    return () => {
      window.removeEventListener("beforeunload", windowAlert)
    }
  }, [windowAlert])

  const { loading: activityLoading, error: activityError } = useStudentActivityWithQuestionsQuery({
    variables: {
      where: { slug: slug }
    },
    onCompleted(data) {
      const localActivity = data.studentActivity
      if (isEmptyString(localActivity?.instructionText) && localActivity?.video === null && localActivity?.instructionImage === null && isEmptyString(localActivity?.description)) {
        if (localActivity?.questions && localActivity?.questions.length > 0) {
          setAnswerSubmissions([{
            "questionId": localActivity?.questions[0].id,
            "answers": []
          }])
          setFooterButtonState(FOOTER_BUTTON_STATE.QA_DISABLED)
        } else {
          studentNextRandomActivityLazyQuery({ variables: { where: { skillId: localActivity.skill?.id } } }).then(response => {
            setNextRandomActivity(response.data?.studentNextRandomActivity)
          })
        }
      }
      if (localActivity?.questions === null || localActivity?.questions?.length === 0) {
        studentNextRandomActivityLazyQuery({ variables: { where: { skillId: localActivity.skill?.id } } }).then(response => {
          setNextRandomActivity(response.data?.studentNextRandomActivity)
        })
      }
      setActivity(localActivity)
    },
  })

  function onChangeAnswer(input: any) {
    const answerSubmission = answerSubmissions.pop()
    if (answerSubmission) {
      if (input.currentTarget.type == "radio" || input.currentTarget.type == "checkbox") {
        if (input.currentTarget.checked) {
          if (input.currentTarget.checked && input.currentTarget.type == "radio") {
            answerSubmission.answers = [input.currentTarget.value]
          } else {
            answerSubmission.answers.push(input.currentTarget.value)
          }
        } else {
          answerSubmission.answers.splice(answerSubmission.answers.findIndex(x => x === input.currentTarget.value), 1)
        }
        setFooterButtonState(FOOTER_BUTTON_STATE.QA_ENABLED)
      } else {
        answerSubmission.answers = [input.target.value.toLowerCase()]
        if (input.target.value.length > 0) {
          setFooterButtonState(FOOTER_BUTTON_STATE.QA_ENABLED)
        } else {
          setFooterButtonState(FOOTER_BUTTON_STATE.QA_DISABLED)
        }
      }
      setAnswerSubmissions([...answerSubmissions, answerSubmission])
    }
  }

  function validateAnswer(answerSubmission: ValidateActivityQuestionSubmitInput) {
    validateActivityQuestionAnswer({
      variables: {
        data: { answers: answerSubmission.answers },
        where: { id: answerSubmission.questionId }
      }
    }).then((res) => {
      if (res.data) {
        let answerText
        addEvent({
          "event": "qa_attempted",
          "user_id": state.user?.id,
          "item": slug,
          "item_id": activity?.id,
          answer: res.data.validateActivityQuestionAnswer.isCorrect ? "correct" : "incorrect"
        })
        if (res.data.validateActivityQuestionAnswer.isCorrect) {
          individualCorrectAudio?.play()
          if (!isEmptyString(res.data.validateActivityQuestionAnswer.answerText)) {
            answerText = res.data?.validateActivityQuestionAnswer.answerText!!
          } else {
            answerText = "Your answer is correct!"
          }
          setFooterButtonState(FOOTER_BUTTON_STATE.CONTINUE)
          const isLastQue = !!(activity!.questions!.length === answerSubmissions.length)
          if (isLastQue) {
            activityQuestionSubmit({
              variables: {
                data: answerSubmissions,
                activity: { id: activity?.id }
              },
              refetchQueries: [{
                query: CURRENT_USER
              }]
            }).then((res) => {
              if (res.data?.activityQuestionSubmit.success) {
                addEvent({
                  "event": "qa_submitted",
                  "user_id": state.user?.id,
                  "item": slug,
                  "item_id": activity?.id
                })
                setFooterButtonState(FOOTER_BUTTON_STATE.CONTINUE)
              }
              else {
                toast("Something went wrong, please try again", {
                  type: "error"
                })
              }
            }).catch((error: Error) => {
              toast(error.message, {
                type: "error"
              })
            })
          }
        } else {
          if (!isEmptyString(res.data.validateActivityQuestionAnswer.incorrectText)) {
            answerText = res.data?.validateActivityQuestionAnswer.incorrectText!!
          } else {
            answerText = "Oh! Your answer is incorrect. Let's watch the tutorial again and attempt the correct answer."
          }
          incorrectAudio?.play()
          setAnswerSubmissions([])
          setFooterButtonState(FOOTER_BUTTON_STATE.QA_FAILED)
        }
        setQuestionResponse({ isCorrect: res.data.validateActivityQuestionAnswer.isCorrect, answerText: answerText })
      }
    })
  }

  function challengeSubmit() {
    setShowUserSubmission(true)
    commentSubmitAudio?.play()
    swiper.slideNext()
  }

  const isMobile = useMediaQuery({ maxWidth: 767 })

  return (
    <Modal show={displayState === DISPLAY_STATE.QA} onHide={() => {
      addEvent({
        "event": "close_activity",
        "user_id": state.user?.id,
        "item": slug,
        "item_id": activity?.id
      })
      closeModalAudio?.play()
      setShowConfirmModal(true)
    }} fullscreen keyboard={false}>
      <Modal.Header closeButton>
        <div className="mx-auto" style={{ width: "80%" }}>
          <ProgressBar now={progress} />
        </div>
      </Modal.Header>
      <Modal.Body>
        <Container className="h-100">
          <Row className="quiz-row">
            <Col xs={{ span: 12 }} sm={{ span: 8, offset: 2 }} className="question-box">
              <Swiper className="" allowTouchMove={false} slidesPerView="auto" style={{ height: "70vh" }}
                onProgress={(_swiper, progress) => setProgress(Math.floor(progress * 100))}
                onSlideNextTransitionStart={(sw) => {
                  if (sw.slides[sw.activeIndex].className.indexOf("qa-success-view") === -1 && sw.slides[sw.activeIndex].className.indexOf("challenge-success") === -1) {
                    continueAudio?.play()
                  }
                  if (sw.slides[sw.activeIndex].className.indexOf("video-view") === -1) {
                    setVideoPlaying(false)
                  }
                  if (sw.slides[sw.activeIndex].className.indexOf("question") > -1) {
                    const questionId = Number.parseInt(sw.slides[sw.realIndex].id)
                    const isQuestionExist = answerSubmissions.find(answerSubmission => answerSubmission.questionId === questionId)
                    if (!isQuestionExist) {
                      setAnswerSubmissions(current => [...current, {
                        "questionId": questionId,
                        "answers": []
                      }])
                    }
                    setFooterButtonState(FOOTER_BUTTON_STATE.QA_DISABLED)
                  } else if (sw.slides[sw.activeIndex].className.indexOf("qa-success-view") > -1) {
                    qaXpsAudio?.play()
                    if (activity?.nextActivity) {
                    } else {
                      studentNextRandomActivityLazyQuery({ variables: { where: { skillId: activity?.skill?.id } } }).then(response => {
                        setNextRandomActivity(response.data?.studentNextRandomActivity)
                      })
                    }
                    if (activity?.isCommentEnabled === false) {
                      setFooterButtonState(FOOTER_BUTTON_STATE.HIDE)
                    }
                  } else if (sw.slides[sw.activeIndex].className.indexOf("challenge-view") > -1) {
                    setShowNextButton(true)
                    setFooterButtonState(FOOTER_BUTTON_STATE.CONTINUE)
                  } else if (sw.slides[sw.activeIndex].className.indexOf("challenge-submit") > -1) {
                    setFooterButtonState(FOOTER_BUTTON_STATE.CONTINUE_DISABLED)
                  } else if (sw.slides[sw.activeIndex].className.indexOf("challenge-success") > -1) {
                    qaXpsAudio?.play()
                    setShowNextButton(false)
                    setFooterButtonState(FOOTER_BUTTON_STATE.CHALLENGE_SUBMIT)
                  }
                  else {
                    setFooterButtonState(FOOTER_BUTTON_STATE.CONTINUE)
                  }
                }}
                onInit={(sw) => {
                  setSwiper(sw)
                }}>

                {(activity?.instructionText || activity?.video) && <SwiperSlide className="video-view">
                  <StyledHeader>Let&apos;s Learn!</StyledHeader>
                  <br />
                  <p>Check the video tutorial to understand the activity before taking a quiz or challenge.</p>
                  {activity?.video && <ReactPlayer
                    playing={videoPlaying}
                    url={activity?.video}
                    height={isMobile ? "auto" : "300px"}
                    width="auto"
                    onPlay={() => setVideoPlaying(!videoPlaying)}
                    config={{ vimeo: { playerOptions: { controls: true } } }}
                  />}
                  <br />
                  {!isEmptyString(activity?.instructionText) && <div dangerouslySetInnerHTML={{ __html: activity?.instructionText!! }} />}
                </SwiperSlide>}

                {(activity?.instructionImage || !isEmptyString(activity?.description)) && <SwiperSlide className="image-view">
                  <StyledHeader>Learn more about it!</StyledHeader>
                  <br />
                  {activity?.instructionImage && <img src={activity?.instructionImage} style={{ maxWidth: "90%" }} />}
                  {!isEmptyString(activity?.description) && <div dangerouslySetInnerHTML={{ __html: activity?.description!! }} />}
                </SwiperSlide>}

                {activity?.questions && activity?.questions.length > 0 && activity?.questions.map((question, index: number) => (<SwiperSlide key={index + 10} id={`${question.id}`} className="question">
                  {questionResponse ? <>
                    {questionResponse.isCorrect ? <Row className="quiz-correct">
                      <Col lg={5} xs={12}>
                        <StyledAnswerImage src="/assets/svg/correct_answer_character.svg" mobileSrc="/assets/svg/correct_answer_character_mobile.svg" />
                      </Col>
                      <StyledAnswerCol lg={7} xs={12}>
                        <StyledAnswerHeader>Correct Answer</StyledAnswerHeader>
                        <div dangerouslySetInnerHTML={{ __html: questionResponse.answerText || "" }} />
                      </StyledAnswerCol>
                    </Row>
                      : <Row className="quiz-incorrect">
                        <Col lg={5} xs={12}>
                          <StyledAnswerImage src="/assets/svg/incorrect_answer_character.svg" mobileSrc="/assets/svg/incorrect_answer_character_mobile.svg" />
                        </Col>
                        <StyledAnswerCol lg={7} xs={12}>
                          <StyledAnswerHeader>Incorrect Answer</StyledAnswerHeader>
                          <div dangerouslySetInnerHTML={{ __html: questionResponse.answerText || "" }} />
                        </StyledAnswerCol>
                      </Row>}
                  </> : <>
                    <StyledHeader>Quiz Time</StyledHeader>
                    <p style={{ fontSize: "1.1rem" }}>Answer the question correctly to earn XPs.</p>
                    <br />
                    {(question.type === "SINGLE_CHOICE" || question.type === "MULTI_CHOICE") && <div>
                      <h2 dangerouslySetInnerHTML={{ __html: question.questionText }}></h2>
                      <div className="mb-3" style={{ display: question.options[0].startsWith("activities/") ? "flex" : "block", flexWrap: "wrap" }}>
                        <br />
                        {question.options.map((optionText: string, index: number) => <QuestionInputContainer key={index}>
                          <HiddenQuestionInput
                            type={question.type === "SINGLE_CHOICE" ? "radio" : "checkbox"}
                            value={Number(index) + 1}
                            name={"question_options" + question.id}
                            id={`question${question.id}-option${index}`}
                            onChange={onChangeAnswer}
                          />
                          <QuestionInputLabel>
                            {optionText.startsWith("activities/") ? <BootstrapImage alt="Option" src={`${NEXT_PUBLIC_SERVER_FILE_ENDPOINT}/${optionText}`} width={200} /> : optionText}
                          </QuestionInputLabel>
                        </QuestionInputContainer>)}
                      </div>
                    </div>}
                    {(question.type === "BLANK" && question.questionText.split("___").length > 0) && <StyledBlankContainer>
                      <span dangerouslySetInnerHTML={{ __html: question.questionText.split("___")[0] }} />
                      <TextStyledInput onChange={onChangeAnswer} />
                      <span dangerouslySetInnerHTML={{ __html: question.questionText.split("___")[1] }} />
                    </StyledBlankContainer>}
                  </>}
                </SwiperSlide>))}

                {activity?.questions && activity?.questions.length > 0 && <SwiperSlide className="qa-success-view">
                  <StyledHeader>{`Yuppie! You earned ${activity?.questionXps} XPs.`}</StyledHeader>
                  <br />
                  <div style={{ textAlign: "center" }}>
                    <Image alt="qa-success.svg" src="/assets/svg/qa-success.svg" width={200} height={200} />
                    <br />
                    <br />
                    {activity?.isCommentEnabled && <div style={{ fontSize: "1.1rem" }}>
                      You&apos;re growing!!!
                      <br />
                      There&apos;s more to come! Complete a challenge to earn more XPs.
                    </div>}
                  </div>
                </SwiperSlide>}

                {activity?.isCommentEnabled && <>
                  <SwiperSlide className="challenge-view">
                    <div>
                      <StyledHeader>Challenge</StyledHeader>
                      <br />
                      {!isEmptyString(activity?.challengeStatement) && <div dangerouslySetInnerHTML={{ __html: activity?.challengeStatement!! }} />}
                      {!isEmptyString(activity?.materialsNeeded) && <div dangerouslySetInnerHTML={{ __html: activity?.materialsNeeded!! }} />}
                      <h3 style={{ color: "#6630da", fontWeight: "bolder", marginTop: "20px" }}>YOU CAN EARN...!</h3>
                      <StyledSkillNameSpan>
                        {activity?.skill && activity?.skill.name && activity?.skill.name.toUpperCase()}
                        <br />
                        +{activity?.xps}XPs
                      </StyledSkillNameSpan>
                    </div>
                    <br />
                    <h4>What others have done?</h4>
                    <ErrorBoundary fallback={ErrorFallback}>
                      <Suspense fallback={<Spinner animation="border" variant="primary" />}>
                        <Comments type={CommentActivityType.ACTIVITY} activitySlug={activity?.slug} />
                      </Suspense>
                    </ErrorBoundary>
                  </SwiperSlide>

                  <SwiperSlide className="challenge-submit">
                    <StyledHeader>Submit Your Challenge</StyledHeader>
                    <br />
                    <CreateComment activityId={activity?.id} slug={slug} challengeSubmit={challengeSubmit} />
                  </SwiperSlide>

                  <SwiperSlide className="challenge-success">
                    <StyledHeader>Congratulations</StyledHeader>
                    <br />
                    <p>Your challenge is successfully submitted.
                      Moderators will review it and once approved you&apos;ll win {activity.xps} XPs. Also, you can showcase it with your friends in the community.
                    </p>
                    {showUserSubmission && <StudentSelfComments type={CommentActivityType.ACTIVITY} activitySlug={activity?.slug} limit={1} />}
                  </SwiperSlide>
                </>}
              </Swiper>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        {showNextButton && <NavButton onClick={() => {
          window.removeEventListener("beforeunload", windowAlert)
          const activeIndexClassName = swiper.slides[swiper.activeIndex].className
          if (activeIndexClassName.indexOf("challenge-view") > -1) {
            addEvent({
              "event": "skip_challenge",
              "user_id": state.user?.id,
              "item": slug,
              "item_id": activity?.id
            })
          } else if (activeIndexClassName.indexOf("challenge-submit") > -1) {
            addEvent({
              "event": "skip_submission",
              "user_id": state.user?.id,
              "item": slug,
              "item_id": activity?.id
            })
          }
          if (activity?.nextActivity) {
            window.location.href = `/skills/${activity?.nextActivity?.skill?.slug}/${activity?.nextActivity.slug}`
          } else {
            window.location.href = `/skills/${nextRandomActivity?.skill?.slug}/${nextRandomActivity?.slug}`
          }
        }}>Skip</NavButton>}
        {footerButtonState === FOOTER_BUTTON_STATE.CONTINUE && <NavButton onClick={() => {
          const activeIndexClassName = swiper.slides[swiper.activeIndex].className
          if (activeIndexClassName.indexOf("video-view") > -1) {
            addEvent({
              "event": "continue_video",
              "user_id": state.user?.id,
              "item": slug,
              "item_id": activity?.id
            })
          } else if (activeIndexClassName.indexOf("image-view") > -1) {
            addEvent({
              "event": "continue_instruction",
              "user_id": state.user?.id,
              "item": slug,
              "item_id": activity?.id
            })
          } else if (activeIndexClassName.indexOf("qa-success-view") > -1) {
            addEvent({
              "event": "continue_qa_completed",
              "user_id": state.user?.id,
              "item": slug,
              "item_id": activity?.id
            })
          } else if (activeIndexClassName.indexOf("challenge-view") > -1) {
            addEvent({
              "event": "continue_challenge",
              "user_id": state.user?.id,
              "item": slug,
              "item_id": activity?.id
            })
          } else if (activeIndexClassName.indexOf("challenge-submit") > -1) {
            addEvent({
              "event": "challenge_submitted",
              "user_id": state.user?.id,
              "item": slug,
              "item_id": activity?.id
            })
          }
          swiper.slideNext()
          setQuestionResponse(null)
        }}>Continue</NavButton>}
        {footerButtonState === FOOTER_BUTTON_STATE.CONTINUE_DISABLED && <NavButton disabled>Continue</NavButton>}
        {footerButtonState === FOOTER_BUTTON_STATE.QA_ENABLED && <NavButton onClick={() => {
          validateAnswer(answerSubmissions[answerSubmissions.length - 1])
        }}>Submit</NavButton>}
        {footerButtonState === FOOTER_BUTTON_STATE.QA_DISABLED && <NavButton disabled>Submit</NavButton>}
        {footerButtonState === FOOTER_BUTTON_STATE.QA_FAILED && <NavButton onClick={() => {
          swiper.slideTo(0)
          setQuestionResponse(null)
          setFooterButtonState(FOOTER_BUTTON_STATE.CONTINUE)
        }}>Retry</NavButton>}
        {footerButtonState === FOOTER_BUTTON_STATE.CHALLENGE_SUBMIT && <NavButton onClick={() => {
          window.removeEventListener("beforeunload", windowAlert)
          addEvent({
            "event": "next_activity",
            "user_id": state.user?.id,
            "from_item": slug,
            "to_item": activity?.nextActivity ? activity?.nextActivity.slug : nextRandomActivity?.slug
          })
          setTimeout(() => {
            if (activity?.nextActivity) {
              window.location.href = `/skills/${activity?.nextActivity?.skill?.slug}/${activity?.nextActivity.slug}`
            } else {
              window.location.href = `/skills/${nextRandomActivity?.skill?.slug}/${nextRandomActivity?.slug}`
            }
          }, 500)
        }}>Continue</NavButton>}
      </Modal.Footer>
    </Modal>
  )
}
